import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const fetchBroadcasts = createAsyncThunk(
  'broadcasts/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBroadcastById = createAsyncThunk(
  'broadcasts/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBroadcastLogs = createAsyncThunk(
  'broadcasts/fetchLogs',
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.getLogs(id, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchApprovedTemplates = createAsyncThunk(
  'broadcasts/fetchApprovedTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.getApprovedTemplates();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createBroadcast = createAsyncThunk(
  'broadcasts/create',
  async (broadcastData, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.create(broadcastData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateBroadcast = createAsyncThunk(
  'broadcasts/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteBroadcast = createAsyncThunk(
  'broadcasts/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.broadcasts.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendBroadcast = createAsyncThunk(
  'broadcasts/send',
  async ({ id, sendNow }, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.send(id, sendNow);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const pauseBroadcast = createAsyncThunk(
  'broadcasts/pause',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.pause(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const resumeBroadcast = createAsyncThunk(
  'broadcasts/resume',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.broadcasts.resume(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  broadcasts: [],
  currentBroadcast: null,
  broadcastLogs: null,
  approvedTemplates: [],
  loading: false,
  logsLoading: false,
  templatesLoading: false,
  error: null,
  filters: {
    status: 'all',
    search: '',
  },
};

const broadcastsSlice = createSlice({
  name: 'broadcasts',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setCurrentBroadcast: (state, action) => {
      state.currentBroadcast = action.payload;
    },
    clearCurrentBroadcast: (state) => {
      state.currentBroadcast = null;
    },
    clearBroadcastLogs: (state) => {
      state.broadcastLogs = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all broadcasts
      .addCase(fetchBroadcasts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBroadcasts.fulfilled, (state, action) => {
        state.loading = false;
        state.broadcasts = action.payload.data || action.payload;
      })
      .addCase(fetchBroadcasts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single broadcast
      .addCase(fetchBroadcastById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBroadcastById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBroadcast = action.payload.data || action.payload;
      })
      .addCase(fetchBroadcastById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch broadcast logs
      .addCase(fetchBroadcastLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(fetchBroadcastLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.broadcastLogs = action.payload.data || action.payload;
      })
      .addCase(fetchBroadcastLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.payload;
      })
      // Fetch approved templates for broadcasting
      .addCase(fetchApprovedTemplates.pending, (state) => {
        state.templatesLoading = true;
      })
      .addCase(fetchApprovedTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.approvedTemplates = action.payload.data || action.payload;
      })
      .addCase(fetchApprovedTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.error = action.payload;
      })
      // Create broadcast
      .addCase(createBroadcast.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBroadcast.fulfilled, (state, action) => {
        state.loading = false;
        const newBroadcast = action.payload.data || action.payload;
        state.broadcasts.unshift(newBroadcast);
        state.error = null;
      })
      .addCase(createBroadcast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update broadcast
      .addCase(updateBroadcast.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBroadcast.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.data || action.payload;
        const index = state.broadcasts.findIndex(b => b._id === updated._id || b.id === updated._id);
        if (index !== -1) state.broadcasts[index] = updated;
        state.error = null;
      })
      .addCase(updateBroadcast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete broadcast
      .addCase(deleteBroadcast.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteBroadcast.fulfilled, (state, action) => {
        state.loading = false;
        state.broadcasts = state.broadcasts.filter(b => b._id !== action.payload && b.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteBroadcast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Send broadcast
      .addCase(sendBroadcast.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendBroadcast.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.data || action.payload;
        const index = state.broadcasts.findIndex(b => b._id === updated._id || b.id === updated._id);
        if (index !== -1) state.broadcasts[index] = updated;
        state.error = null;
      })
      .addCase(sendBroadcast.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Pause broadcast
      .addCase(pauseBroadcast.fulfilled, (state, action) => {
        const updated = action.payload.data || action.payload;
        const index = state.broadcasts.findIndex(b => b._id === updated._id || b.id === updated._id);
        if (index !== -1) state.broadcasts[index] = updated;
      })
      // Resume broadcast
      .addCase(resumeBroadcast.fulfilled, (state, action) => {
        const updated = action.payload.data || action.payload;
        const index = state.broadcasts.findIndex(b => b._id === updated._id || b.id === updated._id);
        if (index !== -1) state.broadcasts[index] = updated;
      });
  },
});

export const { setFilters, setCurrentBroadcast, clearCurrentBroadcast, clearBroadcastLogs, clearError } =
  broadcastsSlice.actions;

export default broadcastsSlice.reducer;
